import React, { useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getTenantType } from '../../config/roleConfig';
import { Header, PageLoading } from '../common';
import {
  SuperAdminSidebar,
  BankAdminSidebar,
  ComplianceMakerSidebar,
  ComplianceCheckerSidebar,
  TreasuryDealerSidebar,
  ForwardsDealerSidebar,
  FamilyOfficeSidebar,
  IndividualInvestorSidebar
} from '../common/Sidebars';
import { EnvironmentBanner } from '../common';

// Base Dashboard Layout
const DashboardLayout = ({ sidebar: Sidebar, allowedRoles, requiredTenantType }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (loading) return <PageLoading />;

  // Safety net: if React state says not authenticated, double-check localStorage.
  if (!isAuthenticated) {
    const savedUser = localStorage.getItem('forwardsflow_user');
    if (savedUser) {
      console.log('DashboardLayout: localStorage has user but state not ready, showing loader');
      return <PageLoading />;
    }
    return <Navigate to="/login" replace />;
  }

  // Check role access
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role)) {
      console.log('Access denied - user role:', user.role, 'allowed roles:', allowedRoles);
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check tenant type (derive from role if missing from stored user)
  const userTenantType = user.tenantType || getTenantType(user.role);
  if (requiredTenantType && userTenantType !== requiredTenantType) {
    console.log('Access denied - user tenantType:', userTenantType, 'required:', requiredTenantType);
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="flex flex-col h-screen bg-zinc-50 overflow-hidden font-sans">
      <EnvironmentBanner />
      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex lg:flex-shrink-0">
          <Sidebar />
        </div>
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
            <div className="fixed inset-y-0 left-0 w-64 bg-white z-50">
              <Sidebar />
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header onMenuClick={() => setMobileMenuOpen(true)} />
          <main className="flex-1 overflow-y-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

// Super Admin Layout
export const SuperAdminLayout = () => (
  <DashboardLayout
    sidebar={SuperAdminSidebar}
    allowedRoles={['forwardsflow_admin', 'platform_admin']}
  />
);

// Bank Admin Layout
export const BankAdminLayout = () => (
  <DashboardLayout
    sidebar={BankAdminSidebar}
    allowedRoles={['bank_admin']}
    requiredTenantType="bank"
  />
);

// Compliance Maker Layout
export const ComplianceMakerLayout = () => (
  <DashboardLayout
    sidebar={ComplianceMakerSidebar}
    allowedRoles={['bank_compliance_maker']}
    requiredTenantType="bank"
  />
);

// Compliance Checker Layout
export const ComplianceCheckerLayout = () => (
  <DashboardLayout
    sidebar={ComplianceCheckerSidebar}
    allowedRoles={['bank_compliance_checker']}
    requiredTenantType="bank"
  />
);

// Treasury Dealer Layout
export const TreasuryDealerLayout = () => (
  <DashboardLayout
    sidebar={TreasuryDealerSidebar}
    allowedRoles={['bank_treasury_dealer']}
    requiredTenantType="bank"
  />
);

// Forwards Dealer Layout
export const ForwardsDealerLayout = () => (
  <DashboardLayout
    sidebar={ForwardsDealerSidebar}
    allowedRoles={['bank_forwards_dealer']}
    requiredTenantType="bank"
  />
);

// Family Office Layout
export const FamilyOfficeLayout = () => (
  <DashboardLayout
    sidebar={FamilyOfficeSidebar}
    allowedRoles={['investor_family_office']}
    requiredTenantType="investor"
  />
);

// Individual Investor Layout
export const IndividualInvestorLayout = () => (
  <DashboardLayout
    sidebar={IndividualInvestorSidebar}
    allowedRoles={['investor_individual']}
    requiredTenantType="investor"
  />
);

export default DashboardLayout;
